<template>
  <div class="levue-page">
    <section class="hero">
      <div class="hero-copy">
        <span class="eyebrow">SMART COMPANION APP</span>
        <div class="brand-lockup">
          <img :src="logoImage" :alt="copy.logoAlt" class="product-logo">
          <h1>{{ copy.productName }}</h1>
        </div>
        <h2>{{ copy.subtitle }}</h2>
        <p>{{ copy.description }}</p>
        <div class="device-tags">
          <span>{{ copy.glasses }}</span>
          <span>{{ copy.earbuds }}</span>
        </div>
      </div>
      <div class="hero-visual">
        <img :src="heroImage" :alt="copy.heroAlt">
      </div>
    </section>

    <section class="features-section">
      <div class="section-heading">
        <span class="section-kicker">{{ copy.featureKicker }}</span>
        <h2>{{ copy.featureTitle }}</h2>
        <p>{{ copy.featureIntro }}</p>
      </div>
      <div class="feature-grid">
        <article v-for="feature in features" :key="feature.title" class="feature-card">
          <div class="feature-image">
            <img :src="feature.image" :alt="feature.title">
          </div>
          <div class="feature-copy">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="download-section">
      <div class="download-copy">
        <span>{{ copy.downloadKicker }}</span>
        <h2>{{ copy.downloadTitle }}</h2>
        <p>{{ copy.downloadDescription }}</p>
      </div>
      <a
        :href="downloadUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="download-qr-link"
        :aria-label="copy.downloadAction"
      >
        <img :src="qrCodeImage" :alt="copy.qrAlt" class="download-qr">
        <span>{{ copy.downloadAction }} <span aria-hidden="true">↗</span></span>
      </a>
    </section>

    <section class="agreement-section">
      <p>{{ copy.legalPrefix }}</p>
      <div class="agreement-links">
        <a :href="userAgreementUrl" target="_blank" rel="noopener noreferrer">{{ copy.terms }}</a>
        <span></span>
        <a :href="privacyPolicyUrl" target="_blank" rel="noopener noreferrer">{{ copy.privacy }}</a>
      </div>
    </section>
  </div>
</template>

<script>
const DOWNLOAD_URL = 'https://app.lizheng.design/levalue/download.html';

const zhCopy = {
  productName: '乐见',
  logoAlt: '乐见 Logo',
  subtitle: '让数字与现实，在眼前无缝相连',
  description: '乐见 Le-Vue 是一款专为智能眼镜打造的创新应用，集智能交互与个性化服务于一体。通过高效语音控制、实时视觉分析及情境感知技术，重新定义您感知世界的方式——无论是通讯、娱乐还是工作，一切尽在眼前。',
  glasses: '智能眼镜',
  earbuds: '智能耳机',
  heroAlt: '乐见智能眼镜、智能耳机与手机应用',
  featureKicker: '六大核心能力',
  featureTitle: '一个应用，连接每个精彩时刻',
  featureIntro: '从语音交互、实时视觉分析到情境感知，乐见让智能能力自然融入通讯、娱乐与工作场景。',
  downloadKicker: '获取乐见',
  downloadTitle: '扫码下载乐见 App',
  downloadDescription: '使用手机扫描二维码进入下载页面，也可以直接点击二维码打开。',
  downloadAction: '打开下载页面',
  qrAlt: '乐见 App 下载二维码',
  legalPrefix: '了解乐见的服务与隐私条款',
  terms: '用户协议',
  privacy: '隐私政策',
  features: [
    ['AI 对话', '随时唤醒智能助手，用自然语言获得清晰、贴合场景的回答。'],
    ['实时翻译', '理解跨语言对话与图像文字，让沟通自然顺畅。'],
    ['智能拍照', '通过智能眼镜或手机快速捕捉眼前画面，轻松保存重要瞬间。'],
    ['高清录音', '清晰记录对话与灵感，为后续转写和整理保留完整内容。'],
    ['便捷录像', '随手记录第一视角影像，留住更真实、更自然的体验。'],
    ['会议总结', '自动转写会议内容，提炼重点并整理行动事项。']
  ]
};

const enCopy = {
  productName: 'Le-Vue',
  logoAlt: 'Le-Vue logo',
  subtitle: 'Where the digital and real worlds meet seamlessly',
  description: 'Le-Vue is an innovative app created for smart glasses, combining intelligent interaction with personalized services. Through efficient voice control, real-time visual analysis, and contextual awareness, Le-Vue redefines how you experience the world—whether communicating, enjoying entertainment, or working, everything is right before your eyes.',
  glasses: 'Smart Glasses',
  earbuds: 'Smart Earbuds',
  heroAlt: 'Le-Vue smart glasses, smart earbuds, and mobile app',
  featureKicker: 'SIX CORE CAPABILITIES',
  featureTitle: 'One app for every meaningful moment',
  featureIntro: 'From voice interaction and real-time visual analysis to contextual awareness, Le-Vue brings intelligence naturally into communication, entertainment, and work.',
  downloadKicker: 'GET LE-VUE',
  downloadTitle: 'Scan to download Le-Vue',
  downloadDescription: 'Scan the QR code with your phone to visit the download page, or click it to open the page directly.',
  downloadAction: 'Open download page',
  qrAlt: 'QR code for downloading the Le-Vue app',
  legalPrefix: 'Learn about Le-Vue services and privacy',
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  features: [
    ['AI Conversation', 'Talk naturally with your AI assistant and receive clear, context-aware answers whenever you need them.'],
    ['Live Translation', 'Understand multilingual conversations and text in images for effortless communication.'],
    ['Smart Photography', 'Capture what you see through your smart glasses or phone and save important moments instantly.'],
    ['Audio Recording', 'Record conversations and ideas clearly, ready for transcription and organization.'],
    ['Video Recording', 'Capture authentic first-person video and preserve experiences as they happen.'],
    ['Meeting Summary', 'Transcribe discussions, extract key points, and organize action items automatically.']
  ]
};

const featureImages = [
  require('@/assets/images/levue/ai-conversation.webp'),
  require('@/assets/images/levue/translation.webp'),
  require('@/assets/images/levue/photo.webp'),
  require('@/assets/images/levue/recording.webp'),
  require('@/assets/images/levue/video.webp'),
  require('@/assets/images/levue/meeting-summary.webp')
];

export default {
  name: 'LeVue',
  data() {
    return {
      heroImage: require('@/assets/images/levue/hero.webp'),
      logoImage: require('@/assets/images/levue/logo.webp'),
      qrCodeImage: require('@/assets/images/levue/download-qr.svg'),
      downloadUrl: DOWNLOAD_URL
    };
  },
  computed: {
    isEnglish() {
      return this.$i18n.locale.indexOf('en') !== -1;
    },
    copy() {
      return this.isEnglish ? enCopy : zhCopy;
    },
    features() {
      return this.copy.features.map((feature, index) => ({
        title: feature[0],
        description: feature[1],
        image: featureImages[index]
      }));
    },
    userAgreementUrl() {
      return this.isEnglish
        ? 'https://app.lizheng.design/levalue/terms-of-service-en.html'
        : 'https://app.lizheng.design/levalue/terms-of-service-zh.html';
    },
    privacyPolicyUrl() {
      return this.isEnglish
        ? 'https://app.lizheng.design/levalue/privacy-policy-en.html'
        : 'https://app.lizheng.design/levalue/privacy-policy-zh.html';
    }
  },
  mounted() {
    document.body.className = 'page-template-default page page-one-column colors-light';
  }
};
</script>

<style lang="stylus" scoped>
.levue-page
  color #14213d
  background #f7f9ff
  overflow hidden

.hero
  min-height 650px
  display grid
  grid-template-columns minmax(0, 0.85fr) minmax(520px, 1.3fr)
  align-items center
  max-width 1440px
  margin 0 auto
  padding 72px 48px 80px
  gap 56px
  position relative

  &::before
    content ''
    position absolute
    width 480px
    height 480px
    border-radius 50%
    background rgba(100, 92, 255, 0.1)
    filter blur(80px)
    left -260px
    top 30px

.hero-copy
  position relative
  z-index 1

  .eyebrow
    display inline-block
    color #5369df
    font-size 12px
    font-weight 700
    letter-spacing 0.18em
    margin-bottom 18px

  h2
    margin 28px 0 18px
    font-size clamp(25px, 2.5vw, 40px)
    line-height 1.25
    letter-spacing -0.03em

  p
    max-width 570px
    margin 0
    color #5c6784
    font-size 17px
    line-height 1.85

.brand-lockup
  display flex
  align-items center
  gap 22px

  h1
    margin 0
    font-size clamp(64px, 7vw, 104px)
    line-height 0.95
    letter-spacing -0.06em
    background linear-gradient(120deg, #142a66 0%, #526de8 50%, #815bdc 100%)
    -webkit-background-clip text
    -webkit-text-fill-color transparent

.product-logo
  width clamp(94px, 9vw, 136px)
  height clamp(72px, 7vw, 104px)
  object-fit contain
  background #f4ffff
  border-radius 22px
  box-shadow 0 16px 36px rgba(43, 117, 220, 0.18)

.device-tags
  display flex
  flex-wrap wrap
  gap 10px
  margin-top 30px

  span
    padding 9px 16px
    color #42517b
    background rgba(255, 255, 255, 0.82)
    border 1px solid rgba(75, 99, 190, 0.14)
    border-radius 999px
    font-size 13px
    box-shadow 0 8px 24px rgba(55, 72, 130, 0.08)

.hero-visual
  position relative
  border-radius 32px
  overflow hidden
  box-shadow 0 36px 80px rgba(28, 38, 101, 0.25)
  transform rotate(1deg)

  &::after
    content ''
    position absolute
    inset 0
    border 1px solid rgba(255, 255, 255, 0.24)
    border-radius inherit
    pointer-events none

  img
    display block
    width 100%
    aspect-ratio 1.7
    object-fit cover

.features-section
  padding 100px 48px 120px
  background #fff

.section-heading
  max-width 720px
  margin 0 auto 56px
  text-align center

  .section-kicker
    color #586fe4
    font-size 12px
    font-weight 700
    letter-spacing 0.16em

  h2
    margin 14px 0 16px
    color #15244a
    font-size clamp(32px, 4vw, 50px)
    line-height 1.2
    letter-spacing -0.04em

  p
    margin 0
    color #71809d
    font-size 17px
    line-height 1.75

.feature-grid
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr))
  max-width 1180px
  margin 0 auto
  gap 24px

.feature-card
  min-width 0
  overflow hidden
  background #f7f9ff
  border 1px solid #e8edfa
  border-radius 24px
  transition transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease

  &:hover
    transform translateY(-7px)
    border-color #d7dffa
    box-shadow 0 24px 56px rgba(36, 55, 112, 0.13)

.feature-image
  height 220px
  overflow hidden
  background #f3f6fb
  padding 10px
  box-sizing border-box

  img
    width 100%
    height 100%
    object-fit contain
    object-position center

.feature-copy
  padding 26px 28px 30px

  h3
    margin 0 0 11px
    color #192b55
    font-size 21px

  p
    margin 0
    color #6b7894
    font-size 15px
    line-height 1.7

.download-section
  max-width 1320px
  margin 0 auto
  padding 84px 70px
  display flex
  align-items center
  justify-content space-between
  gap 52px
  position relative
  border-top 1px solid rgba(65, 83, 140, 0.1)

  &::before
    content ''
    position absolute
    width 330px
    height 330px
    right 8%
    top -90px
    border-radius 50%
    background rgba(100, 92, 255, 0.16)
    filter blur(80px)

.download-copy
  position relative
  max-width 540px

  > span
    color #5369df
    font-size 12px
    font-weight 700
    letter-spacing 0.14em

  h2
    margin 13px 0 14px
    color #14213d
    font-size clamp(34px, 4vw, 52px)
    line-height 1.15
    letter-spacing -0.04em

  p
    margin 0
    color #687492
    font-size 16px
    line-height 1.8

.download-qr-link
  width 250px
  padding 18px 18px 15px
  position relative
  display flex
  flex-direction column
  align-items center
  gap 12px
  box-sizing border-box
  color #253454
  background rgba(255, 255, 255, 0.94)
  border 1px solid rgba(74, 95, 160, 0.16)
  border-radius 24px
  box-shadow 0 18px 48px rgba(35, 53, 105, 0.12)
  text-decoration none
  transition transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease

  &:hover
    transform translateY(-5px)
    border-color #5369df
    box-shadow 0 24px 56px rgba(35, 53, 105, 0.18)

  > span
    font-size 15px
    font-weight 600

.download-qr
  display block
  width 100%
  aspect-ratio 1
  border-radius 12px

.agreement-section
  padding 64px 24px 72px
  text-align center
  background linear-gradient(135deg, #111d46 0%, #242458 52%, #3b2864 100%)
  color #fff

  p
    margin 0 0 18px
    color rgba(255, 255, 255, 0.65)
    font-size 14px

.agreement-links
  display flex
  align-items center
  justify-content center
  gap 18px

  span
    width 1px
    height 14px
    background rgba(255, 255, 255, 0.24)

  a
    color #fff
    font-size 15px
    font-weight 600
    text-decoration none

    &:hover
      color #9ee7ff

@media (max-width: 980px)
  .hero
    grid-template-columns 1fr
    gap 44px
    padding 64px 32px 72px

  .hero-copy
    text-align center

    p
      margin 0 auto

  .device-tags
    justify-content center

  .hero-visual
    max-width 760px
    margin 0 auto

  .feature-grid
    grid-template-columns repeat(2, minmax(0, 1fr))

@media (max-width: 640px)
  .hero
    min-height auto
    padding 52px 18px 56px

  .hero-copy
    h2
      margin-top 22px
      font-size 27px

    p
      font-size 15px

  .brand-lockup
    justify-content center
    gap 14px

    h1
      font-size 58px

  .product-logo
    width 82px
    height 62px
    border-radius 16px

  .hero-visual
    border-radius 22px
    transform none

  .features-section
    padding 72px 18px 84px

  .section-heading
    margin-bottom 38px

  .feature-grid
    grid-template-columns 1fr
    gap 18px

  .feature-card
    display grid
    grid-template-columns 132px 1fr
    align-items stretch
    border-radius 19px

  .feature-image
    height auto
    min-height 156px
    padding 8px

  .feature-copy
    padding 22px 18px

    h3
      font-size 18px

    p
      font-size 14px

  .download-section
    padding 64px 20px 72px
    flex-direction column
    align-items center
    text-align center

  .download-qr-link
    width min(250px, 100%)

@media (max-width: 420px)
  .feature-card
    grid-template-columns 108px 1fr

  .feature-copy
    padding 18px 14px
</style>
